import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, ButtonGroup, Card, Form, Spinner } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import dayjs from "dayjs";
import { notifyError, notifySuccess } from "../../../components/AppToast";
import { ClassCard } from "../components/ClassCard";
import { CreateClassModal } from "../components/CreateClassModal";
import { AvailabilityModal } from "../components/AvailabilityModal";
import { ClassesCalendar } from "../../../components/ClassesCalendar";
import {
  usePlannings,
  useCreatePlanning,
  useReschedulePlanning,
} from "../hooks/usePlannings";
import { useMyAvailability } from "../hooks/useAvailability";

// "HH:MM[:SS]" → minutos desde medianoche.
const toMinutes = (t) => {
  const [h, m] = String(t).split(":").map(Number);
  return h * 60 + (m || 0);
};

// Una clase finalizada (ya inició/pasó o tiene calificaciones) no se puede mover.
const isFinalized = (p) => p?.finalized === true || p?.status !== "active";

// Estados reales que produce el backend (get_status): activa = futura, draft = pasada.
const STATUSES = [
  { key: "active", label: "Programada", color: "#1f9d6b" },
  { key: "draft", label: "Finalizada", color: "#6c757d" },
];

const VIEWS = [
  { key: "combined", label: "Combinado", icon: "mdi-view-split-vertical" },
  { key: "cards", label: "Tarjetas", icon: "mdi-view-grid" },
  { key: "calendar", label: "Calendario", icon: "mdi-calendar-month-outline" },
];

function classTitle(p) {
  const topics = (p.topics ?? []).map((t) => t.name ?? t).filter(Boolean);
  if (topics.length > 0) return topics.join(", ");
  return p.title && p.title !== "Clase programada" ? p.title : "Clase";
}

// Color del evento del calendario: navy para programadas, gris para finalizadas.
function eventPropGetter(event) {
  const p = event.resource;
  const finalized = p?.finalized === true || p?.status !== "active";
  const bg = finalized ? "#6c757d" : "#1f4e79";
  return { style: { backgroundColor: bg, borderColor: bg, color: "#fff" } };
}

function AgendaPage() {
  const [showCreate, setShowCreate] = useState(false);
  // Prefill del modal al crear desde un hueco del calendario (D3); null = botón.
  const [createPrefill, setCreatePrefill] = useState(null);
  const [showAvailability, setShowAvailability] = useState(false);
  const [view, setView] = useState("combined"); // combined | cards | calendar
  const [statusOn, setStatusOn] = useState({ active: true, draft: true });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { slug } = useParams();
  const { data: plannings, isLoading, error } = usePlannings();
  const { data: availability = [] } = useMyAvailability();
  const createMutation = useCreatePlanning();
  const rescheduleMutation = useReschedulePlanning();

  // D8 — sombrea los slots (día/semana) fuera de la disponibilidad del docente.
  // Sin disponibilidad definida, no se sombrea nada.
  const slotPropGetter = useMemo(() => {
    if (!availability.length) return undefined;
    const byDay = {};
    availability.forEach((w) => {
      (byDay[w.weekday] ??= []).push([
        toMinutes(w.start_time),
        toMinutes(w.end_time),
      ]);
    });
    return (date) => {
      const mins = date.getHours() * 60 + date.getMinutes();
      const windows = byDay[date.getDay()] || [];
      const available = windows.some(([s, e]) => mins >= s && mins < e);
      return available
        ? {}
        : { style: { backgroundColor: "rgba(108,117,125,0.10)" } };
    };
  }, [availability]);

  useEffect(() => {
    if (searchParams.get("nueva") === "1") {
      setShowCreate(true);
      searchParams.delete("nueva");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const filtered = useMemo(
    () => (plannings ?? []).filter((p) => statusOn[p.status] ?? true),
    [plannings, statusOn]
  );

  const events = useMemo(
    () =>
      filtered.map((p) => {
        const start = dayjs(`${p.date}T${p.time_start || "00:00"}`).toDate();
        const end = dayjs(start).add(p.duration || 1, "hour").toDate();
        return { id: p.id, title: classTitle(p), start, end, resource: p };
      }),
    [filtered]
  );

  const handleCreate = (data) => {
    createMutation.mutate(data, {
      onSuccess: (res) => {
        setShowCreate(false);
        setCreatePrefill(null);
        // D7 — resumen si fue una serie recurrente (creadas / omitidas).
        const rec = res?._recurrence;
        if (rec) {
          const skipped = rec.skipped?.length ?? 0;
          notifySuccess(
            skipped > 0
              ? `Se crearon ${rec.created} clases (${skipped} omitida(s) por conflicto o fecha pasada).`
              : `Se crearon ${rec.created} clases.`
          );
        }
      },
      // Error de validación (p. ej. conflicto de horario/aula) → toast inmediato,
      // visible sin tener que subir por el formulario. El modal se mantiene abierto.
      onError: (err) => {
        const d = err?.response?.data;
        notifyError(
          d?.detail ?? d?.date?.[0] ?? "No se pudo crear la clase. Revisa los datos.",
          "No se pudo crear la clase"
        );
      },
    });
  };

  const openCreate = () => {
    setCreatePrefill(null);
    setShowCreate(true);
  };

  // D3 — crear arrastrando/haciendo clic en un hueco: abre el modal precargado.
  const handleSelectSlot = ({ start, end }) => {
    const s = dayjs(start);
    const durMin = dayjs(end).diff(s, "minute");
    // En vista Mes el rango es de un día completo (≥24 h): sin hora útil → 08:00, 1 h.
    const dayLevel = durMin >= 24 * 60;
    setCreatePrefill({
      date: s.format("YYYY-MM-DD"),
      time_start: dayLevel ? "08:00" : s.format("HH:mm"),
      duration: dayLevel ? 1 : Math.max(1, Math.round(durMin / 60)),
    });
    setShowCreate(true);
  };

  // D2 — reprogramar (arrastrar) o redimensionar (cambiar duración). Ambos gestos
  // entregan { event, start, end }: la fecha/hora salen de start; la duración, de
  // (end - start). Guardas: no mover finalizadas ni al pasado; el backend además
  // valida conflictos (409) y notifica a los inscritos.
  const handleReschedule = ({ event, start, end }) => {
    const p = event.resource;
    if (isFinalized(p)) {
      notifyError("No puedes mover una clase finalizada.");
      return;
    }
    const s = dayjs(start);
    if (s.isBefore(dayjs())) {
      notifyError("No puedes mover la clase a una fecha u hora pasada.");
      return;
    }
    const duration = Math.max(1, Math.round(dayjs(end).diff(s, "minute") / 60));
    rescheduleMutation.mutate(
      {
        id: event.id,
        date: s.format("YYYY-MM-DD"),
        time_start: s.format("HH:mm"),
        duration,
      },
      {
        onSuccess: (data) => {
          const n = data?._notified ?? 0;
          notifySuccess(
            n > 0
              ? `Clase reprogramada. Se notificó a ${n} estudiante(s).`
              : "Clase reprogramada."
          );
        },
        onError: (err) => {
          notifyError(
            err?.response?.data?.detail ?? "No se pudo reprogramar la clase."
          );
        },
      }
    );
  };

  const goToClass = (event) => navigate(`/${slug}/app/agenda/${event.id}`);
  const toggleStatus = (key) => setStatusOn((s) => ({ ...s, [key]: !s[key] }));

  // Form.Check estándar: check a la izquierda y etiqueta a la derecha ("☑ Programada").
  const renderCheck = (s, idPrefix) => (
    <Form.Check
      key={s.key}
      type="checkbox"
      id={`${idPrefix}-${s.key}`}
      checked={statusOn[s.key]}
      onChange={() => toggleStatus(s.key)}
      label={s.label}
      style={{ accentColor: s.color }}
      className="mb-0"
    />
  );

  const filterPanel = (
    <Card className="border-0 shadow-sm">
      <Card.Body>
        <div
          className="text-uppercase small text-muted fw-semibold mb-3"
          style={{ letterSpacing: ".06em" }}
        >
          Filtrar por estado
        </div>
        <div className="d-flex flex-column align-items-start gap-3">
          {STATUSES.map((s) => renderCheck(s, "filterpanel"))}
        </div>
      </Card.Body>
    </Card>
  );

  // En la barra horizontal, el check va PEGADO a su etiqueta (gap-2) y con más
  // aire entre grupos (gap-4), para que "☑ Programada" se lea como una unidad.
  const renderBarCheck = (s) => (
    <label
      key={s.key}
      htmlFor={`filterbar-${s.key}`}
      className="d-inline-flex align-items-center gap-2 mb-0"
      style={{ cursor: "pointer" }}
    >
      <input
        id={`filterbar-${s.key}`}
        type="checkbox"
        checked={statusOn[s.key]}
        onChange={() => toggleStatus(s.key)}
        style={{
          accentColor: "var(--kp-primary)",
          width: "1.15em",
          height: "1.15em",
          cursor: "pointer",
        }}
      />
      <span>{s.label}</span>
    </label>
  );

  // Versión horizontal de los filtros (para las vistas Tarjetas y Calendario).
  const filterBar = (
    <Card className="border-0 shadow-sm mb-3">
      <Card.Body className="py-3 d-flex align-items-center flex-wrap gap-4">
        <span
          className="text-uppercase small text-muted fw-semibold me-1"
          style={{ letterSpacing: ".06em" }}
        >
          Filtrar por estado
        </span>
        {STATUSES.map((s) => renderBarCheck(s))}
      </Card.Body>
    </Card>
  );

  const emptyFiltered = (
    <Alert variant="light" className="border text-muted mb-0">
      No hay clases con los filtros seleccionados.
    </Alert>
  );

  const calendarBlock = (
    <Card className="border-0 shadow-sm">
      <Card.Body>
        <ClassesCalendar
          events={events}
          onSelectEvent={goToClass}
          eventPropGetter={eventPropGetter}
          slotPropGetter={slotPropGetter}
          defaultView="month"
          selectable
          onSelectSlot={handleSelectSlot}
          onEventDrop={handleReschedule}
          onEventResize={handleReschedule}
          draggableAccessor={(event) => !isFinalized(event.resource)}
        />
        <p className="text-muted small mb-0 mt-2">
          Clic en una clase para ver su detalle · <strong>arrastra</strong> para
          reprogramar · <strong>redimensiona</strong> (vista semana/día) para
          cambiar la duración · clic en un hueco para crear. Navy = programada ·
          gris = finalizada (no editable).
        </p>
      </Card.Body>
    </Card>
  );

  return (
    <>
      <Toaster position="top-right" />
      <div className="container-fluid px-3">
        {/* Parte superior: selector de vista + acción de crear. En móvil, los
            botones de vista y "Disponibilidad" muestran solo el ícono (el
            texto completo desbordaba el ancho de pantalla — feedback real de
            uso); "+ Nueva clase" conserva su texto por ser la acción
            principal. flex-wrap en la fila de controles es un respaldo por si
            un teléfono muy angosto tampoco alcanza ni con los íconos solos. */}
        <div className="d-flex justify-content-between align-items-center py-3 flex-wrap gap-2">
          <h1 className="h4 mb-0">Mi agenda</h1>
          <div className="d-flex align-items-center flex-wrap gap-2">
            <ButtonGroup size="sm" aria-label="Cambiar vista">
              {VIEWS.map((v) => (
                <Button
                  key={v.key}
                  variant={view === v.key ? "primary" : "outline-primary"}
                  onClick={() => setView(v.key)}
                  title={v.label}
                  aria-label={v.label}
                >
                  <i className={`mdi ${v.icon} d-md-none`} aria-hidden="true" />
                  <i className={`mdi ${v.icon} me-1 d-none d-md-inline`} aria-hidden="true" />
                  <span className="d-none d-md-inline">{v.label}</span>
                </Button>
              ))}
            </ButtonGroup>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowAvailability(true)}
              style={{ minHeight: 44 }}
              title="Definir mi disponibilidad semanal"
              aria-label="Disponibilidad"
            >
              <i className="mdi mdi-clock-outline d-md-none" aria-hidden="true" />
              <i className="mdi mdi-clock-outline me-1 d-none d-md-inline" aria-hidden="true" />
              <span className="d-none d-md-inline">Disponibilidad</span>
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={openCreate}
              style={{ minHeight: 44 }}
            >
              + Nueva clase
            </Button>
          </div>
        </div>

        {isLoading && (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" />
            <p className="mt-2 text-muted">Cargando clases…</p>
          </div>
        )}

        {error && (
          <Alert variant="danger">
            No se pudieron cargar las clases. Intenta nuevamente.
          </Alert>
        )}

        {!isLoading && !error && plannings?.length === 0 && (
          <div className="text-center py-5 text-muted">
            <p>No tienes clases programadas.</p>
            <Button
              variant="outline-primary"
              onClick={openCreate}
              style={{ minHeight: 44 }}
            >
              Programar primera clase
            </Button>
          </div>
        )}

        {/* ── Combinado (por defecto): tarjetas a la izquierda + calendario a la derecha ── */}
        {!isLoading && !error && plannings?.length > 0 && view === "combined" && (
          <div className="row g-3">
            <div className="col-lg-4">
              {filterPanel}
              <div
                className="mt-3 pe-1"
                style={{ maxHeight: 640, overflowY: "auto" }}
              >
                {filtered.length === 0
                  ? emptyFiltered
                  : filtered.map((plan) => (
                      <ClassCard
                        key={plan.id}
                        planning={plan}
                        colClassName="mb-3"
                      />
                    ))}
              </div>
            </div>
            <div className="col-lg-8">{calendarBlock}</div>
          </div>
        )}

        {/* ── Solo tarjetas: filtros arriba + cuadrícula ── */}
        {!isLoading && !error && plannings?.length > 0 && view === "cards" && (
          <>
            {filterBar}
            {filtered.length === 0 ? (
              emptyFiltered
            ) : (
              <div className="row">
                {filtered.map((plan) => (
                  <ClassCard key={plan.id} planning={plan} />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Solo calendario: filtros arriba + calendario ── */}
        {!isLoading && !error && plannings?.length > 0 && view === "calendar" && (
          <>
            {filterBar}
            {calendarBlock}
          </>
        )}
      </div>

      <CreateClassModal
        show={showCreate}
        onHide={() => {
          setShowCreate(false);
          setCreatePrefill(null);
        }}
        onCreate={handleCreate}
        isLoading={createMutation.isPending}
        error={createMutation.error}
        initial={createPrefill}
      />

      <AvailabilityModal
        show={showAvailability}
        onHide={() => setShowAvailability(false)}
      />
    </>
  );
}

export { AgendaPage };
